import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, LayoutGrid, CalendarDays } from 'lucide-react';
import PageBanner from '../components/PageBanner';
import Footer from '../components/Footer';
import { getNews } from '../lib/api';
import { IMAGES } from '../mock';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function parseDate(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function NewsCard({ n }) {
  return (
    <Link key={n.id} to={`/news/${n.id}`} data-testid={`news-card-${n.id}`} className="group bg-white rounded-sm overflow-hidden shadow-[0_6px_30px_rgba(0,0,0,0.05)] hover:shadow-[0_14px_44px_rgba(0,0,0,0.12)] transition-shadow duration-500">
      <div className="relative h-[230px] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center transition-transform duration-[900ms] group-hover:scale-105" style={{ backgroundImage: `url(${n.image})` }} />
        <span className="absolute top-4 left-4 bg-[#4a9cd4] text-white font-sans-ah text-[10px] tracking-wide-ah px-3 py-1.5 rounded-sm">{n.tag}</span>
      </div>
      <div className="p-7">
        <p className="font-sans-ah tracking-wide-ah text-[11px] text-[#999] uppercase mb-3">{n.date}</p>
        <h3 className="font-serif-ah text-[24px] text-[#2f2f2f] leading-snug mb-3 group-hover:text-[#4a9cd4] transition-colors">{n.title}</h3>
        <p className="font-sans-ah text-[14px] text-[#666] font-300 leading-relaxed mb-4 line-clamp-3">{n.excerpt}</p>
        <span className="inline-flex items-center gap-2 font-sans-ah text-[12px] tracking-wide-ah text-[#4a9cd4]">READ MORE <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
      </div>
    </Link>
  );
}

function CalendarView({ news }) {
  // Map events by YYYY-M-D key
  const byDay = useMemo(() => {
    const map = {};
    news.forEach((n) => {
      const d = parseDate(n.date);
      if (!d) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      (map[key] = map[key] || []).push(n);
    });
    return map;
  }, [news]);

  const initial = useMemo(() => {
    const dates = news.map((n) => parseDate(n.date)).filter(Boolean).sort((a, b) => b - a);
    return dates[0] || new Date();
  }, [news]);

  const [cursor, setCursor] = useState(new Date(initial.getFullYear(), initial.getMonth(), 1));

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const move = (delta) => setCursor(new Date(year, month + delta, 1));

  return (
    <div data-testid="news-calendar" className="bg-white rounded-sm border border-neutral-100 shadow-[0_6px_30px_rgba(0,0,0,0.05)] p-5 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-serif-ah text-[28px] md:text-[34px] text-[#2f2f2f]" data-testid="calendar-month-label">{MONTHS[month]} {year}</h3>
        <div className="flex gap-2">
          <button onClick={() => move(-1)} data-testid="calendar-prev-btn" aria-label="Previous month" className="w-10 h-10 rounded-full border border-[#4a9cd4] text-[#4a9cd4] flex items-center justify-center hover:bg-[#4a9cd4] hover:text-white transition-colors">
            <ChevronLeft size={19} />
          </button>
          <button onClick={() => move(1)} data-testid="calendar-next-btn" aria-label="Next month" className="w-10 h-10 rounded-full border border-[#4a9cd4] text-[#4a9cd4] flex items-center justify-center hover:bg-[#4a9cd4] hover:text-white transition-colors">
            <ChevronRight size={19} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5 md:gap-2">
        {DOW.map((d) => (
          <div key={d} className="text-center font-sans-ah text-[11px] tracking-wide-ah text-[#999] uppercase pb-2">{d}</div>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} className="min-h-[76px] md:min-h-[104px]" />;
          const key = `${year}-${month}-${d}`;
          const events = byDay[key] || [];
          const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;
          return (
            <div
              key={key}
              data-testid={`calendar-day-${d}`}
              className={`min-h-[76px] md:min-h-[104px] rounded-sm border p-1.5 md:p-2 flex flex-col ${events.length ? 'border-[#cfe3f1] bg-[#f4f9fc]' : 'border-neutral-100'}`}
            >
              <span className={`font-sans-ah text-[12px] mb-1 ${isToday ? 'w-6 h-6 rounded-full bg-[#4a9cd4] text-white flex items-center justify-center' : 'text-[#888]'}`}>{d}</span>
              <div className="flex flex-col gap-1 overflow-hidden">
                {events.map((n) => (
                  <Link
                    key={n.id}
                    to={`/news/${n.id}`}
                    data-testid={`calendar-event-${n.id}`}
                    title={n.title}
                    className="block bg-[#4a9cd4] hover:bg-[#3d7ca6] text-white rounded-[3px] px-1.5 py-1 font-sans-ah text-[10px] md:text-[11px] leading-tight truncate transition-colors"
                  >
                    {n.title}
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function NewsPage() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');

  useEffect(() => {
    getNews().then(setNews).catch(() => setNews([])).finally(() => setLoading(false));
  }, []);

  const toggleCls = (active) =>
    `inline-flex items-center gap-2 font-sans-ah text-[12px] tracking-wide-ah px-4 py-2.5 rounded-sm transition-colors ${
      active ? 'bg-[#4a9cd4] text-white' : 'bg-white text-[#555] border border-neutral-200 hover:border-[#4a9cd4] hover:text-[#4a9cd4]'
    }`;

  return (
    <>
      <PageBanner
        title="News & Events"
        crumb="NEWS & EVENTS"
        subtitle="Discover everything happening across the Almaguin Highlands — events, new members and community updates."
        image={IMAGES.heroC}
      />
      <section className="bg-[#f8f6f2] py-16 md:py-20">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="flex justify-end gap-2 mb-8">
            <button onClick={() => setView('list')} data-testid="news-view-list-btn" className={toggleCls(view === 'list')}>
              <LayoutGrid size={15} /> LIST
            </button>
            <button onClick={() => setView('calendar')} data-testid="news-view-calendar-btn" className={toggleCls(view === 'calendar')}>
              <CalendarDays size={15} /> CALENDAR
            </button>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-3 gap-8">
              {['sk-a', 'sk-b', 'sk-c'].map((k) => (
                <div key={k} className="h-[420px] bg-white rounded-sm animate-pulse" />
              ))}
            </div>
          ) : news.length === 0 ? (
            <p className="text-center font-serif-ah text-[26px] text-[#555] py-20">No news yet. Check back soon!</p>
          ) : view === 'calendar' ? (
            <CalendarView news={news} />
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {news.map((n) => <NewsCard key={n.id} n={n} />)}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}
